import * as React from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarCheck,
  ClipboardList,
  FileText,
  FormInput,
  GraduationCap,
  Images,
  LogOut,
  Scale,
  Settings,
  ShieldCheck,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Badge, cn } from "@alight/ui";

/**
 * HOST PORTAL shell, shared by every site that enrols families.
 *
 * The whole `/admin/*` route group sits behind Cloudflare Access. By the time a
 * request reaches the browser the operator has already authenticated against
 * the identity provider, and the Worker independently verifies the Access JWT
 * on every `/api/admin/*` call (see @alight/enrolment). The UI therefore
 * *assumes* an authenticated operator and simply surfaces the identity
 * Cloudflare injects.
 *
 * Cloudflare Access exposes the current user at the same-origin endpoint
 * `/cdn-cgi/access/get-identity`; we read the email from there and link logout
 * to the standard Access logout endpoint. These `/cdn-cgi/*` routes are a
 * Cloudflare platform concern (not part of the application API), so they live
 * here rather than in the shared contracts client.
 */

/** Subset of the Cloudflare Access identity payload we render. */
interface AccessIdentity {
  email: string;
  name?: string;
}

const ACCESS_IDENTITY_URL = "/cdn-cgi/access/get-identity";
const ACCESS_LOGOUT_URL = "/cdn-cgi/access/logout";

export interface AdminNavLink {
  label: string;
  /** Route path, e.g. "/admin/programs". Both apps register the same paths. */
  to: string;
  icon: LucideIcon;
  /**
   * Highlight ONLY on `to` itself (plus `activeUnder`), not on every path
   * beneath it. Needed for a link to the portal's index ("/admin"), which
   * otherwise prefix-matches every screen and stays highlighted everywhere.
   * Unset = the long-standing prefix match, so existing nav lists are
   * unaffected.
   */
  exact?: boolean;
  /** With `exact`: also highlight under these paths, e.g. the inbox under
   *  "/admin/messages" (its message detail pages). */
  activeUnder?: string[];
}

/**
 * The registration portal's own sections, in the order an operator works
 * through them: what came in, who it was for, did they turn up, did they pay,
 * then the things that shape all of it.
 */
export const ENROLMENT_ADMIN_LINKS: AdminNavLink[] = [
  { label: "Submissions", to: "/admin/submissions", icon: ClipboardList },
  { label: "Children", to: "/admin/students", icon: Users },
  { label: "Attendance", to: "/admin/attendance", icon: CalendarCheck },
  { label: "Reconciliation", to: "/admin/reconciliation", icon: Scale },
  { label: "Invoices", to: "/admin/invoices", icon: FileText },
  { label: "Programs", to: "/admin/programs", icon: GraduationCap },
  { label: "Assets", to: "/admin/assets", icon: Images },
  { label: "Forms", to: "/admin/forms", icon: FormInput },
  { label: "Settings", to: "/admin/settings", icon: Settings },
];

async function fetchAccessIdentity(): Promise<AccessIdentity | null> {
  // Served by CF Access in production; 404s in local dev — degrade quietly.
  const res = await fetch(ACCESS_IDENTITY_URL, {
    headers: { Accept: "application/json" },
    credentials: "include",
  });
  if (!res.ok) return null;
  const data = (await res.json()) as Partial<AccessIdentity> | null;
  if (!data || typeof data.email !== "string") return null;
  return { email: data.email, name: typeof data.name === "string" ? data.name : undefined };
}

export interface AdminLayoutProps {
  /** Sub-heading under "Host Portal", e.g. "Holy Trinity admin". */
  orgLabel: string;
  /**
   * Nav sections. Defaults to the registration portal's own; a site with extra
   * host tools (htbh's message inbox) passes its own ordering.
   */
  links?: AdminNavLink[];
  /** The routed screen — the app passes its router's <Outlet />. */
  children: React.ReactNode;
}

export function AdminLayout({ orgLabel, links = ENROLMENT_ADMIN_LINKS, children }: AdminLayoutProps) {
  const { data: identity, isLoading } = useQuery({
    queryKey: ["admin", "access-identity"],
    queryFn: fetchAccessIdentity,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-8 md:flex-row md:gap-10 md:px-6">
      <AdminSidebar
        orgLabel={orgLabel}
        links={links}
        identity={identity ?? null}
        isLoading={isLoading}
      />
      <main className="min-w-0 flex-1 pt-2">{children}</main>
    </div>
  );
}

function AdminSidebar({
  orgLabel,
  links,
  identity,
  isLoading,
}: {
  orgLabel: string;
  links: AdminNavLink[];
  identity: AccessIdentity | null;
  isLoading: boolean;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <aside className="md:w-64 md:flex-shrink-0">
      <div className="md:sticky md:top-8">
        <div className="flex items-center gap-2">
          <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-card bg-primary text-primary-fg">
            <ShieldCheck className="h-5 w-5" aria-hidden />
          </span>
          <div className="leading-tight">
            <p className="font-heading text-lg font-extrabold text-ink">Host Portal</p>
            <p className="text-xs font-medium text-muted">{orgLabel}</p>
          </div>
        </div>

        <nav aria-label="Admin sections" className="mt-6">
          <ul className="flex flex-row flex-wrap gap-1 md:flex-col">
            {links.map(({ label, to, icon: Icon, exact, activeUnder }) => (
              <li key={to}>
                <Link
                  to={to}
                  activeOptions={{ exact: exact ?? false }}
                  className={cn(
                    exact && isUnder(pathname, activeUnder) && "active",
                    "flex items-center gap-3 rounded-card px-3 py-2 text-sm font-semibold",
                    "text-ink/80 transition-colors hover:bg-primary/5 hover:text-ink",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    "[&.active]:bg-primary/10 [&.active]:text-primary",
                  )}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" aria-hidden />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <AdminIdentityCard identity={identity} isLoading={isLoading} />
      </div>
    </aside>
  );
}

/** True when `pathname` is one of `prefixes` or below one of them. */
function isUnder(pathname: string, prefixes: string[] | undefined): boolean {
  const path = pathname.replace(/\/+$/, "");
  return (prefixes ?? []).some((p) => {
    const prefix = p.replace(/\/+$/, "");
    return path === prefix || path.startsWith(`${prefix}/`);
  });
}

function AdminIdentityCard({
  identity,
  isLoading,
}: {
  identity: AccessIdentity | null;
  isLoading: boolean;
}) {
  return (
    <div className="mt-8 rounded-card border border-border bg-card p-4 shadow-soft">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-wide text-muted">Signed in</p>
        <Badge variant="outline" className="gap-1">
          <ShieldCheck className="h-3 w-3" aria-hidden />
          Access
        </Badge>
      </div>

      <div className="mt-2 min-w-0">
        {isLoading ? (
          <p className="text-sm text-muted">Verifying identity…</p>
        ) : identity ? (
          <>
            {identity.name ? (
              <p className="truncate text-sm font-semibold text-ink" title={identity.name}>
                {identity.name}
              </p>
            ) : null}
            <p className="truncate text-sm text-ink/80" title={identity.email}>
              {identity.email}
            </p>
          </>
        ) : (
          <p className="text-sm text-muted">Cloudflare Access identity unavailable.</p>
        )}
      </div>

      <a
        href={ACCESS_LOGOUT_URL}
        onClick={() => {
          // Symmetric with the site nav's Sign out: clear the site's own
          // magic-link session too, so leaving the admin portal doesn't
          // leave "My account" signed in behind it. Fire-and-forget — the
          // Access logout navigation proceeds regardless.
          void fetch("/api/auth/signout", { method: "POST" }).catch(() => {});
        }}
        className={cn(
          "mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-muted",
          "transition-colors hover:text-primary",
          "focus-visible:underline focus-visible:outline-none",
        )}
      >
        <LogOut className="h-3.5 w-3.5" aria-hidden />
        Sign out
      </a>
    </div>
  );
}
